import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { useLeadActions } from './leads/hooks/useLeadActions';
import { useLeadsFilter } from './leads/hooks/useLeadsFilter';
import LeadsHeader from './leads/components/LeadsHeader';
import LeadsContent from './leads/components/LeadsContent';
import LeadsSearchBar from './leads/components/LeadsSearchBar';
import { getCompanyMembers } from '@/services/companyMemberService';
import { getLeads } from '@/services/leads';
import { Lead } from '@/types/lead';
import { useLeadsSearch } from './leads/hooks/useLeadsSearch';
import { useSubscription } from '@/hooks/useSubscription';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';


const Index = () => {
  const { toast } = useToast();
  const { user, isLoading: authLoading, activeCompany } = useAuth();
  const { subscription } = useSubscription(user?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(false);
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchAllLeads = async () => {
    // Don't fetch if no company
    if (!activeCompany?.id) {
      console.log('Skipping lead fetch: No active company');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setFetchError(null);
    try {
      const leads = await getLeads(activeCompany.id);
      setAllLeads(leads);
    } catch (e) {
      setFetchError('Failed to load leads.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchAllLeads();
      
      // Check for payment success and verify order
      const handlePaymentSuccess = async () => {
        if (searchParams.get('payment_success') === 'true') {
          const orderId = searchParams.get('order_id');
          const sid = searchParams.get('sid');
          
          setShowSuccessMessage(true);
          
          // Verify the Revolut order
          if (orderId && sid) {
            try {
              await supabase.functions.invoke('verify-revolut-order', {
                body: { order_id: orderId, subscription_id: sid }
              });
              
              toast({
                title: "Payment Successful!",
                description: "Your subscription is now active.",
              });
            } catch (error) {
              console.error('Payment verification error:', error);
              toast({
                title: "Payment Received",
                description: "Your subscription is being processed.",
              });
            }
          }
          
          // Clean URL parameters after 5 seconds
          setTimeout(() => {
            setSearchParams({});
            setShowSuccessMessage(false);
          }, 5000);
        }
      };
      
      handlePaymentSuccess();
    }
  }, [user, activeCompany?.id, authLoading, searchParams, toast, setSearchParams]);

  const {
    searchQuery,
    setSearchQuery,
    filteredLeads,
    clearSearch
  } = useLeadsSearch({ allLeads });

  const {
    handleToggleFavorite,
    handleDelete,
    handleAddNote,
    handleAddDocument
  } = useLeadActions(setAllLeads);

  const {
    filteredLeads: fullyFilteredLeads,
    showFavoritesOnly,
    setShowFavoritesOnly,
    selectedCategory,
    setSelectedCategory,
    categories,
    selectedUserId,
    setSelectedUserId,
    currentView,
    setCurrentView
  } = useLeadsFilter(filteredLeads, user, activeCompany?.id);

  const fetchCompanyUsers = async () => {
    if (!activeCompany) return;
    try {
      setIsLoadingUsers(true);
      const users = await getCompanyMembers(activeCompany.id);
      setCompanyUsers(users.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Error fetching company users:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load team members. Please try again.",
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Fetch leads and company users - leads via separate effect for cleaner dependency management
  useEffect(() => {
    if (user && activeCompany) {
      fetchCompanyUsers();
    }
    // eslint-disable-next-line
  }, [user, activeCompany]);

  const handleImportComplete = () => {
    fetchAllLeads();
  };

  if (authLoading || !user || !activeCompany) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            {authLoading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Initializing...</p>
              </>
            ) : (
              <>
                <p className="text-lg font-medium">No Company Selected</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Please select a company to view leads
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-1 container mx-auto p-4 sm:p-6 lg:p-8 pb-24 md:pb-8 w-full overflow-x-hidden">
      {showSuccessMessage && subscription && subscription.tier !== 'free' && (
        <div className="mb-8 p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 border-2 border-green-200 dark:border-green-800 rounded-xl shadow-lg animate-fade-in elevation-lifted">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-green-900 dark:text-green-100 tracking-tight">
                Subscription Activated Successfully! 🎉
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Your <span className="capitalize font-semibold">{subscription.tier}</span> subscription is now active and ready to use. Enjoy all premium features!
              </p>
            </div>
            <button 
              onClick={() => setShowSuccessMessage(false)}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 text-xl font-semibold transition-transform hover:scale-110 flex-shrink-0"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div id="mobile-search-section">
        <LeadsSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          clearSearch={clearSearch}
        />
      </div>
      
      <div className="w-full overflow-x-hidden">
        <LeadsHeader
          currentView={currentView}
          setCurrentView={setCurrentView}
          totalLeads={fullyFilteredLeads.length}
          showFavoritesOnly={showFavoritesOnly}
          toggleFavoritesFilter={() => setShowFavoritesOnly(prev => !prev)}
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          isLoading={isLoading}
          companyUsers={companyUsers}
          selectedUserId={selectedUserId}
          onUserSelect={setSelectedUserId}
          isLoadingUsers={isLoadingUsers}
          filteredLeads={fullyFilteredLeads}
          onImportComplete={handleImportComplete}
        />
        <LeadsContent
          isLoading={isLoading}
          error={fetchError}
          filteredLeads={fullyFilteredLeads}
          currentView={currentView}
          searchQuery={searchQuery}
          clearSearch={clearSearch}
          isSearching={false}
          handleSearch={() => undefined}
          handleToggleFavorite={handleToggleFavorite}
          handleDelete={handleDelete}
          handleAddNote={handleAddNote}
          handleAddDocument={handleAddDocument}
        />
      </div>
    </main>
  );
};

export default Index;
